'use client'
import React, { ReactNode } from 'react'
import {
	ApolloClient,
	ApolloProvider,
	createHttpLink,
	DefaultContext,
	from,
	GraphQLRequest,
	InMemoryCache,
	NormalizedCacheObject,
	split,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient } from 'graphql-ws'
import { getMainDefinition } from '@apollo/client/utilities'

import { GRAPHQL_URL, SUBSCRIPTION_URL } from '@/shared/constants'
import { clearCookie, showToast } from '@/shared/utils'
import { getToken } from '@/shared/libs/token'
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries'
import { createHash } from 'crypto'
import lazy from 'next/dynamic'
import Loading from '@/shared/components/loading'

// const GoogleAnalytics = lazy(() => import('@/shared/components/scripts/googleAnalytics'), { ssr: false, loading: () => <Loading /> })
const Toast = lazy(() => import('@/shared/components/Toast'), { ssr: false, loading: () => <Loading /> })
const NProgressBar = lazy(() => import('@/shared/components/NProgress'), { ssr: false, loading: () => <Loading /> })

const apiUrl = GRAPHQL_URL
const subscriptionUrl = SUBSCRIPTION_URL
const httpLink = createHttpLink({
	uri: apiUrl,
	useGETForQueries: true,
})

const wsLink = () => {
	return new GraphQLWsLink(
		createClient({
			url: subscriptionUrl,
		})
	)
}

const splitLink =
	typeof window === 'undefined'
		? httpLink
		: split(
				({ query }) => {
					const definition = getMainDefinition(query)
					return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
				},
				wsLink(),
				httpLink
		  )

type MyApolloProps = {
	children: ReactNode
	dispatch?: (action: any) => void
}
type MyApolloProviderType = {
	props: MyApolloProps
}

class MyApolloProvider extends React.Component<MyApolloProps, MyApolloProviderType> {
	middleware: any
	errorLink: any
	graphqlClient: ApolloClient<NormalizedCacheObject>
	constructor(props: MyApolloProps) {
		super(props as MyApolloProps)
		this.middleware = setContext((operation: GraphQLRequest, previous: DefaultContext) => {
			// console.log({ previous })
			if (!['GetPreviewArticleFront', 'GetPreviewFantasyArticleFront'].includes(operation?.operationName as string)) {
				const token = getToken()
				// console.log({ token })
				return {
					headers: {
						authorization: token && `${token}`,
					},
				} as Promise<DefaultContext> | DefaultContext
			}
			return previous
		})

		this.errorLink = onError(({ graphQLErrors, networkError, operation }) => {
			if (graphQLErrors && operation.operationName !== 'UserLogout') {
				graphQLErrors.forEach(({ message, extensions }) => {
					if (extensions?.code === 'UNAUTHENTICATED') {
						// unauthorized
						clearCookie()
						// this.props?.router?.replace('/')
						// history.replace('/')
					}
					if (message !== '502: Bad Gateway') {
						// showToast({ message, toastType: 'danger' })
					}
					{extensions &&
					showToast({ heading: extensions.code as string, message: `${extensions.serviceName}  ${message}`, toastType: 'danger' })
					}
					console.log({ message })
				})
			}
			if (networkError) console.error(`[Network error]: ${networkError}`)
		})
		this.graphqlClient = new ApolloClient({
			link: from([
				this.errorLink,
				this.middleware,
				createPersistedQueryLink({
					sha256: (val) => (typeof window === 'undefined' ? createHash('sha256').update(val).digest('hex') : hashValue(val)),
					useGETForHashedQueries: true,
				}),
				splitLink,
			]),
			defaultOptions: {
				watchQuery: { errorPolicy: 'all' },
				query: { errorPolicy: 'all' },
				mutate: { errorPolicy: 'all' },
			},
			cache: new InMemoryCache(),
			connectToDevTools: true,
		})
	}

	render() {
		return (
			<>
				<ApolloProvider client={this.graphqlClient}>{this.props.children}</ApolloProvider>
					<Toast />
					{/* <GoogleAnalytics /> */}
					<NProgressBar />
			</>
		)
	}
}

async function hashValue(val: any) {
	return crypto.subtle.digest('SHA-256', new TextEncoder().encode(val)).then((h) => {
		const hexes = [],
			view = new DataView(h)
		for (let i = 0; i < view.byteLength; i += 4) hexes.push(('00000000' + view.getUint32(i).toString(16)).slice(-8))
		return hexes.join('')
	})
}

export default MyApolloProvider
